import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';

/**
 * A row in the config registry list.
 *
 * `parseError` is non-null for rows whose stored text is not JSON. Those predate
 * the registry — TM_URL held a bare URL for years — and the list still shows
 * them, because a row you cannot open is a row you cannot fix.
 */
export interface ConfigSummary {
  id: number;
  key: string;
  version: number;
  hasSchema: boolean;
  /** True for keys the server refuses to write. Readable and diffable, not editable. */
  isProtected: boolean;
  protectedReason: string | null;
  versionCount: number;
  parseError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigDetail extends ConfigSummary {
  /** The parsed document. Null when `parseError` is set. */
  value: unknown;
  /** The exact stored text, which is what the JSON tab edits. */
  rawValue: string;
  schema: unknown | null;
}

export interface ConfigVersionAuthor {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

/** A history entry without its payload — the list endpoint stays cheap. */
export interface ConfigVersionSummary {
  version: number;
  note: string | null;
  /** Set when this version was produced by restoring an earlier one. */
  restoredFromVersion: number | null;
  createdAt: string;
  createdBy: ConfigVersionAuthor | null;
  isCurrent: boolean;
}

export interface ConfigVersionDetail extends ConfigVersionSummary {
  value: unknown;
  rawValue: string;
  schema: unknown | null;
  parseError: string | null;
}

/**
 * Query keys for the registry.
 *
 * `list` is keyed on the search term because the server does the filtering — the
 * key set is small, but filtering server-side keeps the one client rule that
 * matters: values are never searched, only keys. TM_CREDENTIALS holds a secret.
 */
export const configRegistryKeys = {
  all: ['ConfigRegistry'] as const,
  list: (search: string) => ['ConfigRegistry', 'list', search] as const,
  detail: (key: string) => ['ConfigRegistry', 'detail', key] as const,
  versions: (key: string) => ['ConfigRegistry', 'versions', key] as const,
  version: (key: string, version: number) => ['ConfigRegistry', 'versions', key, version] as const,
};

export const useConfigsQuery = (search: string) => {
  const { sessionState } = useAuth();
  const isReady = isSignedIn(sessionState);

  return useQuery({
    queryKey: configRegistryKeys.list(search),
    queryFn: async () => {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const { data } = await get<ConfigSummary[]>(`/v1.0/internals/configs${query}`);
      return data;
    },
    enabled: isReady,
  });
};

export default useConfigsQuery;
